/* AttachmentPicker — upload PDF/DOCX/TXT/images as AI reference material.
 * Extracts text server-side (/ai/attachments/extract) and hands the combined
 * text back via onChange. Previously uploaded documents are saved to the
 * teacher's library and reusable via "Browse saved". */
import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtDate } from "@/lib/dateUtils";
import { Paperclip, X, Loader2, FileText, FolderOpen, Trash2, Check } from "lucide-react";

export default function AttachmentPicker({ onChange, dark = false, compact = false }) {
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]); // {id?, name, text, chars}
  const [busy, setBusy] = useState(false);
  const [browsing, setBrowsing] = useState(false);
  const [saved, setSaved] = useState(null); // null = not loaded
  const [loadingId, setLoadingId] = useState("");

  const emit = (list) => {
    setFiles(list);
    onChange?.(list.map((f) => `--- ${f.name} ---\n${f.text}`).join("\n\n").slice(0, 16000));
  };

  const pick = async (e) => {
    const fl = Array.from(e.target.files || []);
    e.target.value = "";
    if (!fl.length) return;
    setBusy(true);
    const added = [];
    for (const f of fl.slice(0, 4)) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/ai/attachments/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push({ id: data.id, name: data.name, text: data.text, chars: data.chars });
      } catch (err) {
        toast.error(`${f.name}: ${err.response?.data?.detail || "could not read file"}`);
      }
    }
    setBusy(false);
    if (added.length) {
      emit([...files, ...added].slice(0, 5));
      setSaved(null); // refresh list next time Browse opens
    }
  };

  const openBrowse = async () => {
    const next = !browsing;
    setBrowsing(next);
    if (next && saved === null) {
      try {
        const { data } = await api.get("/ai/attachments");
        setSaved(data.attachments || []);
      } catch { setSaved([]); }
    }
  };

  const attachSaved = async (att) => {
    if (files.some((f) => f.id === att.id || f.name === att.name)) { toast("Already attached"); return; }
    setLoadingId(att.id);
    try {
      const { data } = await api.get(`/ai/attachments/${att.id}`);
      const d = data.attachment;
      emit([...files, { id: d.id, name: d.name, text: d.text, chars: d.chars }].slice(0, 5));
    } catch { toast.error("Could not load document"); }
    finally { setLoadingId(""); }
  };

  const deleteSaved = async (e, att) => {
    e.stopPropagation();
    try {
      await api.delete(`/ai/attachments/${att.id}`);
      setSaved((s) => (s || []).filter((x) => x.id !== att.id));
    } catch { toast.error("Delete failed"); }
  };

  const remove = (i) => emit(files.filter((_, idx) => idx !== i));

  const border = dark ? "border-white/15 text-white/70 hover:border-white/40" : "border-neutral-300 text-neutral-600 hover:border-neutral-500";
  const panel = dark ? "bg-[#0D1117] border-white/15" : "bg-white border-neutral-200";
  const row = dark ? "hover:bg-white/5 text-white/80" : "hover:bg-neutral-50 text-neutral-700";
  return (
    <div data-testid="attachment-picker">
      <input ref={inputRef} type="file" multiple accept=".pdf,.docx,.txt,.md,.csv,.png,.jpg,.jpeg,.webp" className="hidden" onChange={pick} />
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid="attach-file-btn"
          className={`inline-flex items-center gap-2 text-sm font-bold border-2 rounded-md px-4 ${compact ? "py-2" : "py-2.5"} transition-colors ${border}`}
        >
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Paperclip size={15} />}
          {busy ? "Reading…" : "Attach material (PDF · Word · image)"}
        </button>
        <button
          type="button"
          onClick={openBrowse}
          data-testid="browse-saved-btn"
          className={`inline-flex items-center gap-2 text-sm font-bold border-2 rounded-md px-4 ${compact ? "py-2" : "py-2.5"} transition-colors ${browsing ? "border-[#0EA5E9] text-[#0EA5E9]" : border}`}
        >
          <FolderOpen size={15} /> Browse saved
        </button>
      </div>
      {browsing && (
        <div className={`mt-2 border rounded-md overflow-hidden max-h-52 overflow-y-auto ${panel}`} data-testid="saved-attachments-panel">
          {saved === null ? (
            <div className={`px-3 py-3 text-xs inline-flex items-center gap-2 ${dark ? "text-white/50" : "text-neutral-500"}`}><Loader2 size={12} className="animate-spin" /> Loading…</div>
          ) : saved.length === 0 ? (
            <div className={`px-3 py-3 text-xs ${dark ? "text-white/50" : "text-neutral-500"}`}>No saved documents yet — upload once and it will appear here for reuse.</div>
          ) : saved.map((att) => {
            const attached = files.some((f) => f.id === att.id || f.name === att.name);
            return (
              <button key={att.id} type="button" onClick={() => attachSaved(att)} data-testid={`saved-attachment-${att.id}`}
                className={`w-full px-3 py-2 flex items-center gap-2 text-left text-xs transition-colors ${row}`}>
                {loadingId === att.id ? <Loader2 size={12} className="animate-spin shrink-0" /> : attached ? <Check size={12} className="text-emerald-500 shrink-0" /> : <FileText size={12} className="shrink-0 opacity-60" />}
                <span className="flex-1 truncate font-medium">{att.name}</span>
                <span className="opacity-50 font-mono">{Math.round((att.chars || 0) / 100) / 10}k · {fmtDate(att.created_at)}</span>
                <span role="button" tabIndex={-1} onClick={(e) => deleteSaved(e, att)} data-testid={`saved-attachment-del-${att.id}`}
                  className="opacity-40 hover:opacity-100 hover:text-red-500 p-0.5"><Trash2 size={11} /></span>
              </button>
            );
          })}
        </div>
      )}
      {files.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {files.map((f, i) => (
            <span key={i} data-testid={`attachment-chip-${i}`} className={`inline-flex items-center gap-1.5 text-[11px] rounded-full px-2.5 py-1 ${dark ? "bg-white/10 text-white/80" : "bg-neutral-100 text-neutral-700"}`}>
              <FileText size={10} /> {f.name} <span className="opacity-50">({Math.round(f.chars / 100) / 10}k)</span>
              <button onClick={() => remove(i)} className="opacity-60 hover:opacity-100"><X size={10} /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
